import clsx from 'clsx'
import React, { FC, useMemo } from 'react'

import { useUserContext } from '@/context'
import { TicketCommentReaction } from '@/types/TicketCommentReaction'

import { ReactionBadge } from './ReactionBadge'
import { groupReactionsByEmoji } from './reactionUtils'

interface ReactionListProps {
  className?: string
  onAddReaction?: (emoji: string) => void
  onRemoveReaction?: (reactionId: number) => void
  reactions?: TicketCommentReaction[]
  supportedReactions?: string[] | null
}

/**
 * Displays grouped emoji reactions for a ticket comment.
 * Groups reactions by emoji and shows count for each.
 * Returns null if no reactions to display.
 */
export const ReactionList: FC<ReactionListProps> = ({
  reactions,
  supportedReactions,
  onRemoveReaction,
  className = '',
}) => {
  const { user } = useUserContext()
  const currentUserId = user?.id

  const groupedReactions = useMemo(
    () => groupReactionsByEmoji(reactions || [], currentUserId),
    [reactions, currentUserId],
  )

  // Don't render if no reactions
  if (!reactions || reactions.length === 0) {
    return null
  }

  const canInteract = !!supportedReactions && supportedReactions.length > 0

  const handleRemove = (emoji: string) => {
    if (!onRemoveReaction || !currentUserId) return

    // Find the current user's reaction for this emoji
    const userReaction = reactions.find((r) => r.emoji === emoji && r.createdBy === currentUserId)
    if (userReaction) {
      onRemoveReaction(userReaction.id)
    }
  }

  return (
    <div className={clsx('flex flex-wrap gap-1 mt-2', className)}>
      {groupedReactions.map((reaction) => (
        <ReactionBadge
          canRemove={canInteract}
          key={reaction.emoji}
          onRemove={() => handleRemove(reaction.emoji)}
          reaction={reaction}
        />
      ))}
    </div>
  )
}
